import { useState, useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson } from '@atlassian/jira-fetch';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
	X2C_SERVICE_URL_PREFIX,
	isSynthetic,
} from '@atlassian/jira-project-create-importer-common';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, readCSVInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import type { MigrationId, AdvancedOptions, ProjectData } from '../../common/types';
import { transformToDateProperties } from '../../common/utils';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { CSVData } from '../../common/utils/csv-import-clients/types';
import type { UseReadCSVPreview } from './types';
import { transformToMappingStateWithSuggestedDateFormat } from './utils';

const getX2CFetchCSVPreview = (cloudId: string, migrationId: MigrationId) =>
	`${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/csv/${migrationId}/preview`;

export const fetchCSVPreview = (
	cloudId: string,
	migrationId: MigrationId,
	advancedOptions: AdvancedOptions,
	projectData: ProjectData,
): Promise<CSVData> =>
	fetchJson(getX2CFetchCSVPreview(cloudId, migrationId), {
		method: 'POST',
		body: JSON.stringify({
			fileEncoding: advancedOptions.fileEncoding.trim(),
			delimiter: advancedOptions.delimiter,
			dateProperties: transformToDateProperties(advancedOptions),
			projectDetails: {
				type: projectData.type,
				style: projectData.style,
				template: projectData.template,
			},
		}),
		headers: {
			'x-issynthetic-traffic': String(isSynthetic()),
		},
	});

export const useReadCSVPreview = (source: Source, serviceName?: string): UseReadCSVPreview => {
	const cloudId = useCloudId();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.READ_CSV,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
	const [errorCode, setErrorCode] = useState(null);

	const analtyticsAttributes = useAnalyticsAttributesContext();
	const client = useGetCSVImportClient(source, serviceName);

	const readCSVPreview = useCallback(
		async (
			migrationId: MigrationId,
			advancedOptions: AdvancedOptions,
			projectData: ProjectData,
		) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			readCSVInteractionMetric.start();
			onExperienceStart();

			try {
				let csvData: CSVData;
				if (ff('enable-importing-data-to-existing-project_k8uub')) {
					csvData = await client.getPreview({
						migrationId,
						advancedOptions,
						projectData,
					});
				} else {
					csvData = await fetchCSVPreview(cloudId, migrationId, advancedOptions, projectData);
				}
				readCSVInteractionMetric.stop();
				onExperienceSuccess({ migrationId, ...analtyticsAttributes });

				setIsLoading(false);
				return transformToMappingStateWithSuggestedDateFormat(csvData);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				readCSVInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, {
					migrationId,
					...analtyticsAttributes,
				});
				fireErrorAnalytics({
					meta: {
						id: 'read-csv-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
				setErrorCode(error.statusCode);
				return null;
			}
		},
		[
			analtyticsAttributes,
			client,
			cloudId,
			onExperienceFail,
			onExperienceStart,
			onExperienceSuccess,
		],
	);

	return { isLoading, hasErrorOccurred, errorCode, readCSVPreview };
};
